import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dayIdLog } from '../lib/dayIdLog';
import { fetchUserPreferences } from '../lib/userPreferences';
import { createPreferenceSync } from '../lib/preferenceSync';
import { onSignOut } from '../lib/signOutReset';
import {
  type RecoveryProtocol,
  type RecoveryDay,
  DEFAULT_PROTOCOLS,
  DEFAULT_RECOVERY_ICON,
  sumRecoveryXP,
  recoveryStreak,
  recoveryAdherence,
} from '../utils/recovery';
import { toLocalYMD, shiftYMD } from '../utils/hydration';

const HISTORY_DAYS = 60;

interface RecoveryRow {
  log_date: string;
  done_ids: string[] | null;
  scheduled: number | null;
}

function rowToDay(row: RecoveryRow): RecoveryDay {
  return { date: row.log_date, doneIds: Array.isArray(row.done_ids) ? row.done_ids : [], scheduled: row.scheduled ?? 0 };
}

interface RecoveryStore {
  protocols: RecoveryProtocol[];
  todayDate: string;
  todayDoneIds: string[];
  todayScheduled: number;
  history: RecoveryDay[];

  isLoading: boolean;
  isInitialized: boolean;
  initializedUserId: string | null;

  initialize: () => Promise<void>;
  /** Re-read today's row (the tab came back — lib/dayLogFocusRefresh). Read only. */
  refreshToday: () => Promise<void>;
  toggleDone: (id: string) => Promise<void>;
  /** Mark one done for today — idempotent, never a flip (habit ticks, finished routines, energy protocols). */
  markDone: (id: string) => Promise<void>;
  addProtocol: (p: Omit<RecoveryProtocol, 'id' | 'active'>) => void;
  updateProtocol: (id: string, patch: Partial<Omit<RecoveryProtocol, 'id'>>) => void;
  deleteProtocol: (id: string) => void;
  toggleActive: (id: string) => void;

  getRecoveryXP: () => number;
  getStreak: () => number;
  getTodayAdherence: () => { done: number; scheduled: number; pct: number };
  clearStore: () => void;
}

const activeCount = (protocols: RecoveryProtocol[]) => protocols.filter(p => p.active).length;

function withTodayInHistory(history: RecoveryDay[], today: RecoveryDay): RecoveryDay[] {
  const others = history.filter(d => d.date !== today.date);
  return [...others, today].sort((a, b) => a.date.localeCompare(b.date));
}

/** Today's done_ids: one id on or off in the SERVER's list (lib/dayIdLog). */
const dayLog = dayIdLog({
  table: 'recovery_logs',
  column: 'done_ids',
  what: 'recovery',
  scheduled: () => activeCount(useRecoveryStore.getState().protocols),
  show: (date, ids) => {
    const s = useRecoveryStore.getState();
    const scheduled = activeCount(s.protocols);
    useRecoveryStore.setState({ todayDate: date, todayDoneIds: ids, todayScheduled: scheduled, history: withTodayInHistory(s.history, { date, doneIds: ids, scheduled }) });
  },
  loadedFor: () => useRecoveryStore.getState().initializedUserId,
});

/**
 * The protocols are `user_preferences.recovery_protocols`, merged per protocol on
 * every write (lib/preferenceSync.ts) — a stale tab can no longer put back one
 * deleted elsewhere or drop one added there.
 * ⚠️ An EMPTY saved list is the user's choice: only a never-saved one (null)
 * starts from the defaults. Deleting every protocol used to bring them back.
 */
const protocolsSync = createPreferenceSync<RecoveryProtocol[]>({
  column: 'recovery_protocols',
  rule: { records: 'id' },
  decode: (saved) => (Array.isArray(saved) ? (saved as RecoveryProtocol[]) : DEFAULT_PROTOCOLS.map(p => ({ ...p }))),
  read: () => useRecoveryStore.getState().protocols,
  adopt: (protocols) => applyProtocols(useRecoveryStore.setState, useRecoveryStore.getState, protocols),
  label: 'recovery protocols',
  loadedFor: () => useRecoveryStore.getState().initializedUserId,
});

/** Await the pending protocols write (tests). */
export const flushRecoveryProtocols = (): Promise<void> => protocolsSync.flush();

export const useRecoveryStore = create<RecoveryStore>((set, get) => ({
  protocols: DEFAULT_PROTOCOLS.map(p => ({ ...p })),
  todayDate: toLocalYMD(new Date()),
  todayDoneIds: [],
  todayScheduled: activeCount(DEFAULT_PROTOCOLS),
  history: [],

  isLoading: false,
  isInitialized: false,
  initializedUserId: null,

  initialize: () => dayLog.load(async () => {
    const at = dayLog.generation();
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== dayLog.generation()) return;
    if (get().initializedUserId === user.id) return;

    set({ isLoading: true });
    try {
      const prefs = await fetchUserPreferences();

      const todayStr = toLocalYMD(new Date());
      const since = shiftYMD(todayStr, -HISTORY_DAYS);
      const { data, error } = await supabase
        .from('recovery_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', since)
        .order('log_date', { ascending: true });
      // Signed out while reading: the next account must not get this one's ticks — nor its
      // list as the base (so the list is loaded only after this).
      if (at !== dayLog.generation()) return;
      if (error) throw error;
      const protocols = protocolsSync.load(prefs?.row?.recovery_protocols ?? null, prefs?.row?.updated_at);

      const rows = (data ?? []) as RecoveryRow[];
      const history = rows.map(rowToDay);
      const todayRow = rows.find(r => r.log_date === todayStr);
      const todayDoneIds = Array.isArray(todayRow?.done_ids) ? (todayRow!.done_ids as string[]) : [];
      const todayScheduled = activeCount(protocols);

      set({
        protocols,
        todayDate: todayStr,
        todayDoneIds,
        todayScheduled,
        history: withTodayInHistory(history, { date: todayStr, doneIds: todayDoneIds, scheduled: todayScheduled }),
        isLoading: false,
        isInitialized: true,
        initializedUserId: user.id,
      });
      dayLog.loaded(todayStr, todayDoneIds);
    } catch (err) {
      if (at !== dayLog.generation()) return; // the next account's load may be running: leave its flag and base alone
      console.error('Error initializing recovery:', err);
      protocolsSync.reset(); // the list was never adopted — its base must not stand either
      set({ isLoading: false });
    }
  }),

  refreshToday: () => dayLog.refresh(),

  // ⚠️ Flips what THIS screen shows — applied to the server's list, so a stale screen can
  // only ever set the one id the user tapped (never re-send or drop the others).
  toggleDone: async (id: string) => {
    await dayLog.set(id, !get().todayDoneIds.includes(id));
  },

  markDone: async (id: string) => {
    await dayLog.set(id, true);
  },

  addProtocol: (p) => {
    const full: RecoveryProtocol = {
      id: crypto.randomUUID(),
      name: p.name.trim() || 'Protocol',
      icon: p.icon || DEFAULT_RECOVERY_ICON,
      active: true,
    };
    persistProtocols(set, get, [...get().protocols, full]);
  },

  updateProtocol: (id, patch) => {
    persistProtocols(set, get, get().protocols.map(p => (p.id === id ? { ...p, ...patch } : p)));
  },

  deleteProtocol: (id) => {
    persistProtocols(set, get, get().protocols.filter(p => p.id !== id));
  },

  toggleActive: (id) => {
    persistProtocols(set, get, get().protocols.map(p => (p.id === id ? { ...p, active: !p.active } : p)));
  },

  getRecoveryXP: () => sumRecoveryXP(get().history),
  getStreak: () => recoveryStreak(get().history, get().todayDate, shiftYMD),
  getTodayAdherence: () => {
    const { todayDoneIds, todayScheduled } = get();
    return { done: todayDoneIds.length, scheduled: todayScheduled, pct: recoveryAdherence(todayDoneIds.length, todayScheduled) };
  },

  clearStore: () => {
    dayLog.clear();
    protocolsSync.reset();
    set({
      protocols: DEFAULT_PROTOCOLS.map(p => ({ ...p })),
      todayDate: toLocalYMD(new Date()),
      todayDoneIds: [],
      todayScheduled: activeCount(DEFAULT_PROTOCOLS),
      history: [],
      isLoading: false,
      isInitialized: false,
      initializedUserId: null,
    });
  },
}));

// The protocols + a refreshed today-scheduled, in one set.
function applyProtocols(
  set: (partial: Partial<RecoveryStore>) => void,
  get: () => RecoveryStore,
  protocols: RecoveryProtocol[]
) {
  const scheduled = activeCount(protocols);
  const state = get();
  const todayDay: RecoveryDay = { date: state.todayDate, doneIds: state.todayDoneIds, scheduled };
  set({ protocols, todayScheduled: scheduled, history: withTodayInHistory(state.history, todayDay) });
}

function persistProtocols(
  set: (partial: Partial<RecoveryStore>) => void,
  get: () => RecoveryStore,
  protocols: RecoveryProtocol[]
) {
  protocolsSync.recordBase(get().protocols);
  applyProtocols(set, get, protocols);
  protocolsSync.schedule();
}

onSignOut(() => useRecoveryStore.getState().clearStore());
